import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Content, type BuilderContent } from '@builder.io/sdk-angular';
import { BuilderService } from '../../services/builder.service';
import { AudienceService } from '../../services/audience.service';

/**
 * BlogPromoSectionComponent
 * 
 * Renders published blog-promo-section entries filtered by brand and sorted by sequence
 * 
 * Brand filtering based on audience:
 * - High School (bfwpub): Shows entries with Brand = "BFW"
 * - Higher Ed (macmillan): Shows entries with Brand = "ML"
 * 
 * Follows Builder.io Gen2 SDK best practices for fetching multiple entries
 */
@Component({
  selector: 'app-blog-promo-section',
  templateUrl: './blog-promo-section.component.html',
  styleUrls: ['./blog-promo-section.component.scss'],
  standalone: true,
  imports: [CommonModule, Content]
})
export class BlogPromoSectionComponent implements OnInit {
  @Input() apiKey!: string;
  
  model = 'blog-promo-section';
  sections: BuilderContent[] = [];
  loading = true;
  
  // Brand codes for filtering
  private readonly BRAND_BFW = 'BFW';
  private readonly BRAND_ML = 'ML';
  
  constructor(
    private builderService: BuilderService,
    private audienceService: AudienceService
  ) {}
  
  ngOnInit() {
    this.fetchAllSections();
  }

  /**
   * Get the brand code that matches the current audience
   */
  private getAudienceBrand(): string {
    return this.audienceService.isHighSchool() ? this.BRAND_BFW : this.BRAND_ML;
  }

  /**
   * Extract brand name from a section's site reference field
   */
  private getSectionBrand(section: BuilderContent): string | null {
    const siteRef = section.data?.['site'];
    return siteRef?.value?.name || null;
  }

  /**
   * Fetches all published blog promo sections, filters by brand, and sorts by sequence
   */
  private async fetchAllSections() {
    this.loading = true;
    
    try {
      const allSections = await this.builderService.getMultipleContent(this.model, {
        limit: 100,
        includeRefs: true
      });
      
      const audienceBrand = this.getAudienceBrand();
      
      this.sections = allSections
        .filter(section => {
          if (section.published !== 'published') return false;
          const sectionBrand = this.getSectionBrand(section);
          if (!sectionBrand) return false;
          return sectionBrand.toUpperCase() === audienceBrand.toUpperCase();
        })
        .sort((a, b) => {
          const seqA = a.data?.['sequence'] || 999;
          const seqB = b.data?.['sequence'] || 999;
          return seqA - seqB;
        });
      
    } catch (error) {
      console.error('BlogPromoSection: Error fetching sections:', error);
      this.sections = [];
    } finally {
      this.loading = false;
    }
  }
}

